/** @forge-header
 * @id          sportTableHeader
 * @role        Header bar for sport tables | title, game count, compact toggle, collapse, loading
 * @pattern     component
 * @tier        T3
 * @purity      none
 * @upstream    sportsDiscovery:getSeriesTicker,Sport
 *              | sportsStream:SportsLoadingState
 *              | ./LoadingPhaseStatus:LoadingPhaseStatus
 * @downstream  ConsolidatedMoneylines
 * @exports     SportTableHeader:component | SportTableHeaderProps:interface
 * @test        untested
 */

import { cn } from '@/lib/infra/utils';
import type { Sport } from '@/lib/sportsDiscovery';
import { ChevronsUpDown, ChevronDown, Minus } from 'lucide-react';
import type { SportsLoadingState } from '@/lib/sportsStream';
import { LoadingPhaseStatus } from './LoadingPhaseStatus';

export interface SportTableHeaderProps {
  sport?: Sport;
  gameCount: number;
  seriesTicker: string | null;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  compact?: boolean;
  onToggleCompact?: () => void;
  isLoading?: boolean;
  loadingState?: SportsLoadingState;
}

export function SportTableHeader({
  sport = 'nba',
  gameCount,
  seriesTicker,
  collapsed = false,
  onToggleCollapsed,
  compact = false,
  onToggleCompact,
  isLoading = false,
  loadingState,
}: SportTableHeaderProps) {
  const sportLabel = sport.toUpperCase().replace('-', ' ');

  return (
    <div className={cn('border-border border-b px-4 transition-all', compact ? 'py-1.5' : 'py-4')}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2
            className={cn(
              'text-foreground font-semibold transition-all',
              compact ? 'text-sm' : 'text-lg',
              onToggleCollapsed && 'cursor-pointer'
            )}
            onClick={onToggleCollapsed}
          >
            {sportLabel} Moneylines
          </h2>
          {collapsed && (
            <span className="text-muted-foreground text-xs">
              {gameCount} game{gameCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {onToggleCompact && !collapsed && (
            <button
              onClick={onToggleCompact}
              className={cn(
                'text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors',
                compact && 'text-foreground'
              )}
              title={compact ? 'Comfortable spacing' : 'Compact spacing'}
            >
              {compact ? (
                <ChevronsUpDown className="h-3.5 w-3.5" />
              ) : (
                <Minus className="h-3.5 w-3.5" />
              )}
            </button>
          )}
          {seriesTicker && (
            <span className="text-muted-foreground font-mono text-xs">{seriesTicker}</span>
          )}
          <LoadingPhaseStatus
            isLoading={isLoading}
            loadingState={loadingState}
          />
          {onToggleCollapsed && (
            <button
              onClick={onToggleCollapsed}
              className="text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors"
              title={collapsed ? 'Expand section' : 'Collapse section'}
            >
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
